import React, { FC, useEffect } from 'react'
import { Redirect, useLocation } from 'react-router'

import {
  useGetProjectSharesQuery,
  useRenameProjectMutation,
  GetProjectsQueryVariables,
  GetProjectsDocument,
} from 'gqlTypes'
import { hasGraphQLErrorCode } from 'graphql/utils/graphqlErrors'

import DocumentItemsLayout from 'modules/shares/components/DocumentItemsLayout'
import ErrorMessage from 'components/ErrorMessage'
import { handleFetchMore } from 'components/InfiniteList'
import DocumentHead from 'components/DocumentHead'
import DocumentHeader from 'components/DocumentHeader'

// Containers
import { useBanner } from 'containers/Banner'

// Icons
import { ReactComponent as WarningTriangle } from 'icons/warning-triangle-64.svg'

import EmptyState from 'modules/shares/components/EmptyState'
import { useToast } from 'containers/ToastManager'
import { routes } from 'constants/routes'

// Helpers

const typeInline = <T extends any>(variables: T) => variables

const DRAFTS_HEADER_COPY = {
  title: 'My Drafts',
  description:
    'Only you can see documents that you keep here, unless you share them.',
}

const EMPTY_STATE_COPY = {
  standard: {
    editor: {
      title: 'No documents in this project yet',
      description:
        'Add a document by saving it in the Mac app — or move an existing Workspace document here.',
    },
    viewer: {
      title: 'No documents in this project yet',
      description: 'Only Editors can create documents.',
    },
  },
  drafts: {
    editor: {
      title: 'No documents added to My Drafts yet',
      description:
        'Use My Drafts to store your unfinished or private documents. Only you can see documents that you keep in My Drafts — unless you choose to share them.',
    },
    /**
     * Viewers can also have a drafts folder in some scenarios.
     * For example: when a user is a normal editor with a Drafts folder but gets downgraded to viewer,
     * it keeps the drafts folder even if it is empty.
     */
    viewer: {
      title: "You haven't added anything to My Drafts",
      description: 'Only Editors can create documents.',
    },
  },
}

function getEmptyStateContent(isDraftsProject: boolean, userIsEditor: boolean) {
  const projectType = isDraftsProject ? 'drafts' : 'standard'
  const userType = userIsEditor ? 'editor' : 'viewer'

  return EMPTY_STATE_COPY[projectType][userType]
}

export interface ProjectSharesProps {
  projectId: string
  workspaceId: string
  search?: string
  userIsEditor: boolean
  userCanRenameProject: boolean
}

const entriesPath = ['project', 'shares', 'entries']

const ProjectShares: FC<ProjectSharesProps> = props => {
  const {
    workspaceId,
    projectId,
    search,
    userIsEditor,
    userCanRenameProject,
  } = props
  const location = useLocation()
  const { showToast } = useToast()
  const { showBanner } = useBanner()

  const variables = {
    shortId: projectId,
    after: null,
    search: {
      name: search || null,
      isCurrentVersionDownloadable: null,
    },
  }

  const { data, networkStatus, fetchMore, error } = useGetProjectSharesQuery({
    variables,
    shouldInvalidatePrevious: (prev, curr) => prev?.shortId !== curr?.shortId,
  })

  const [renameProject] = useRenameProjectMutation({
    redirectErrors: true,
    onError: 'show-toast',
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: typeInline<GetProjectsQueryVariables>({
          workspaceId,
        }),
      },
    ],
    awaitRefetchQueries: true,
    optimisticResponse: ({ name }) => ({
      __typename: 'RootMutationType',
      renameProject: {
        __typename: 'ProjectRenamePayload',
        errors: [],
        successful: false,
        project: {
          __typename: 'Project',
          name,
          shortId: projectId,
          identifier: project!.identifier,
          type: project!.type,
        },
      },
    }),
    onCompleted: () => {
      showToast('Project was renamed successfully')
    },
  })

  const project = data && data.project

  useEffect(() => {
    if (project?.deletedAt) {
      const { dismissBanner } = showBanner({
        type: 'information',
        message: (
          <>
            The <b>“Sketch Concepts”</b> project was deleted.{' '}
            <b>Restore the project</b>, to edit its documents.
          </>
        ),
      })

      return dismissBanner
    }
  }, [showBanner, project])

  if (error) {
    if (hasGraphQLErrorCode(error, 'NOT_FOUND')) {
      return (
        <ErrorMessage
          icon={<WarningTriangle />}
          title="Project not available"
          description="The Project you're looking for might have been deleted, or you don't have the right permissions to view it."
        />
      )
    }

    return (
      <ErrorMessage.Generic title="Something went wrong while loading shares" />
    )
  }

  /* Redirect to the Trash Project View if the project has been soft-deleted */
  if (
    project?.deletedAt &&
    location.pathname ===
      routes.WORKSPACE_PROJECT.create({ workspaceId, projectId })
  ) {
    return (
      <Redirect
        to={routes.WORKSPACE_TRASH_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }

  const shares = (project && project.shares.entries) || []
  const after = data?.project.shares.meta.after as string | null
  const loadMoreHandler = handleFetchMore(fetchMore, entriesPath, {
    after,
  })

  const loading = networkStatus < 3
  const isDraftsProject = data?.project.type === 'PERSONAL_DRAFTS'

  if (!loading && shares.length === 0) {
    const content = getEmptyStateContent(isDraftsProject, userIsEditor)

    return (
      <EmptyState
        search={search}
        title={content.title}
        description={content.description}
        icon={isDraftsProject ? 'notes' : 'document'}
      />
    )
  }

  const totalShareCount = data?.project.shares.meta.totalCount || 0

  const headerCopy = isDraftsProject
    ? DRAFTS_HEADER_COPY
    : { title: project?.name || '' }

  // Hide the Edit UI if the user doesn't have these permissions
  let handleEdit
  if (userCanRenameProject) {
    handleEdit = async (title: string) => {
      await renameProject({
        variables: { name: title, projectId: project!.identifier },
      })
    }
  }

  return (
    <>
      <DocumentHead title={headerCopy.title} />
      <DocumentItemsLayout
        loading={loading}
        shares={shares}
        onLoadMore={loadMoreHandler}
        dropdownMenu="document"
        showProjectName={false}
        totalShareCount={totalShareCount}
        header={
          <DocumentHeader
            {...headerCopy}
            titlePlaceholder="Project name"
            onEdit={handleEdit}
          />
        }
      />
    </>
  )
}

export default ProjectShares
